/**
 * Facility Storage Location Site Map – shared types.
 *
 * Mirrors the Tier II facility site map (see tierii-shared/types/site-map.ts) but is scoped to
 * one map per facility (not per year) and centred on RCRA storage-area classification instead of
 * chemical inventory: every `facilityStorageLocations` row (central accumulation area, satellite
 * accumulation area, permitted storage, or other) can be placed as a marker on the map, alongside
 * generic site-plan elements (building outline, entrances, pathways, evacuation routes).
 */

import type { FacilityStorageAreaType } from './db.ts';
import type { ISO8601Timestamp } from './common.ts';

/** Every feature drawn on the storage-location site map has one of these types. */
export type StorageSiteMapFeatureType =
  | 'central-accumulation-area' // Point – RCRA 90-day central accumulation area
  | 'satellite-accumulation-area' // Point – RCRA satellite accumulation area
  | 'permitted-storage-area' // Point – permitted hazardous waste storage
  | 'other-storage-area' // Point – other storage location (SPCC tank, Tier II vessel, etc.)
  | 'pathway' // Polyline – access route to a storage location
  | 'evacuation-route' // Polyline – exit path for first responders / facility personnel
  | 'building-outline' // Polygon – outer footprint of a building or structure
  | 'room' // Polygon – interior room / area boundary
  | 'entrance' // Point – door or entrance (incl. emergency exits)
  | 'water-supply' // Point – water supply for firefighting (e.g. fire hydrant), per 40 CFR
  // 262.262(c)(6) "Quick Reference Guide" element: locations of water supply (flow rate noted
  // in the feature's `note`).
  | 'notification-system' // Point – on-site notification system (e.g. fire alarm, smoke alarm),
  // per 40 CFR 262.262(c)(7).
  | 'annotation'; // Point or Polygon – free-form text label / callout

/** Maps a storage location's RCRA `areaType` to its site-map feature type. */
export const AREA_TYPE_TO_FEATURE_TYPE: Record<FacilityStorageAreaType, StorageSiteMapFeatureType> = {
  'central-accumulation': 'central-accumulation-area',
  satellite: 'satellite-accumulation-area',
  'permitted-storage': 'permitted-storage-area',
  other: 'other-storage-area',
};

/** Properties carried by every StorageSiteMap GeoJSON feature. */
export interface StorageSiteMapFeatureProperties {
  featureType: StorageSiteMapFeatureType;
  /** Human-readable label shown on the map and in the legend. */
  label?: string;
  /** Stroke / fill colour override (CSS hex string). */
  color?: string;
  /** Extra notes printed in the legend. */
  note?: string;
  /** Links a storage-area marker back to its `facilityStorageLocations` row. */
  facilityStorageLocationID?: number;
}

/** A single GeoJSON feature with strongly-typed properties. */
export interface StorageSiteMapFeature {
  type: 'Feature';
  geometry:
    | { type: 'Point'; coordinates: [number, number] }
    | { type: 'LineString'; coordinates: [number, number][] }
    | { type: 'Polygon'; coordinates: [number, number][][] };
  properties: StorageSiteMapFeatureProperties;
}

/** The full GeoJSON FeatureCollection stored for a facility's storage-location site map. */
export interface StorageSiteMapFeatureCollection {
  type: 'FeatureCollection';
  features: StorageSiteMapFeature[];
}

/** The full storage-location site map record (mirrors the DB row). */
export interface FacilityStorageLocationSiteMapRecord {
  facilityID: number;
  centerLat: number;
  centerLng: number;
  zoom: number;
  features: StorageSiteMapFeatureCollection;
  updatedAt: ISO8601Timestamp | null;
}

/** A storage location available to place on the map (drag-and-drop source). */
export interface StorageSiteMapLocationItem {
  facilityStorageLocationID: number;
  name: string;
  areaType: FacilityStorageAreaType | null;
  isHazWasteRegulated: boolean;
  isSPCCRegulated: boolean;
  isTier2Regulated: boolean;
}

/** Default colours by feature type (used by the editor). */
export const STORAGE_SITE_MAP_COLORS: Record<StorageSiteMapFeatureType, string> = {
  'central-accumulation-area': '#dc2626', // red-600
  'satellite-accumulation-area': '#d97706', // amber-600
  'permitted-storage-area': '#7c3aed', // violet-600
  'other-storage-area': '#0891b2', // cyan-600
  'water-supply': '#0284c7', // sky-600
  'notification-system': '#ea580c', // orange-600
  pathway: '#2563eb', // blue-600
  'evacuation-route': '#16a34a', // green-600
  'building-outline': '#374151', // gray-700
  room: '#6b7280', // gray-500
  entrance: '#059669', // emerald-600
  annotation: '#1f2937', // gray-800
};

/** Human-readable labels for each feature type (used in legend/sidebar). */
export const STORAGE_SITE_MAP_LABELS: Record<StorageSiteMapFeatureType, string> = {
  'central-accumulation-area': 'Central Accumulation Area (90-day)',
  'satellite-accumulation-area': 'Satellite Accumulation Area',
  'water-supply': 'Water Supply (e.g. Fire Hydrant)',
  'notification-system': 'On-Site Notification System',
  'permitted-storage-area': 'Permitted Storage Area',
  'other-storage-area': 'Other Storage Location',
  pathway: 'Access Pathway',
  'evacuation-route': 'Evacuation Route',
  'building-outline': 'Building Outline',
  room: 'Room / Area',
  entrance: 'Entrance / Doorway',
  annotation: 'Annotation',
};
